import React, { useState, useEffect } from 'react';
import Dropzone from 'react-dropzone';
import { Button, Card, Col, Row, Input, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { getIn } from 'formik';
import FeatherIcon from "feather-icons-react";
import { formatBytes } from '../../../digi/ResidentApplicationForm/Helpers';
import axios from '../../../api/axios';
import { isLoading } from '../../../../store/actions';
import { useDispatch } from 'react-redux';
import { DocumentPreviewUI, canPreviewFile, cleanFileName } from './DocumentRenderer';
import Swal from 'sweetalert2';

const GET_PREVIEW_URL = "/property/get-content";

const DocumentsHandler = ({ name, label, formik }) => {
    const dispatch = useDispatch();
    const [isDragActive, setIsDragActive] = useState(false);
    const [previews, setPreviews] = useState([]);
    const [selectedFileForPreview, setSelectedFileForPreview] = useState(null);

    // For editing filenames
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingFile, setEditingFile] = useState(null);
    const [editedFileName, setEditedFileName] = useState("");
    const [fileIndex, setFileIndex] = useState(null);

    // Load existing files with signed URLs
    useEffect(() => {
        const loadSignedUrls = async () => {
            const files = formik?.values[name];

            if (files && Array.isArray(files) && files.length > 0 && previews.length === 0) {
                const baseUrl = "https://rentdigicare.tor1.digitaloceanspaces.com/";
                const filesToProcess = [];

                for (const item of files) {
                    if (!item?.originalUrl) continue;
                    const relativePath = item.originalUrl.replace(baseUrl, "");
                    filesToProcess.push({
                        originalUrl: item.originalUrl,
                        relativePath,
                        name: item.name || cleanFileName(item.originalUrl.split('/').pop() || 'File'),
                        fileType: item.fileType || null,
                    });
                }

                console.log({ filesToProcess })

                if (filesToProcess.some(f => f.relativePath)) {
                    dispatch(isLoading(true));
                    try {
                        const processedFiles = await Promise.all(
                            filesToProcess.map(async (item) => {
                                try {
                                    const { data } = await axios.get(GET_PREVIEW_URL, {
                                        params: { fileUrl: item.relativePath },
                                    });

                                    return {
                                        preview: data.signedUrl,
                                        name: item.name,
                                        originalUrl: item.originalUrl,
                                        originalName: item.name,
                                        fileType: item.fileType,
                                    };
                                } catch (error) {
                                    console.error('Error fetching signed URL:', error);
                                    return null;
                                }
                            })
                        );

                        const validFiles = processedFiles.filter(file => file !== null);
                        setPreviews(validFiles);
                    } catch (error) {
                        console.error('Error processing files:', error);
                    } finally {
                        dispatch(isLoading(false));
                    }
                } else {
                    setPreviews(files);
                }
            }
        };

        if (formik?.values[name] && previews.length === 0) {
            loadSignedUrls();
        }
    }, [formik?.values[name], previews.length, dispatch, name]);

    // Cleanup previews on component unmount
    useEffect(() => {
        return () => {
            previews.forEach(fileObj => {
                if (fileObj.preview && typeof fileObj.preview !== 'string') {
                    URL.revokeObjectURL(fileObj.preview);
                }
            });
        };
    }, [previews]);

    if (!formik) return null;

    const { setFieldValue, setFieldTouched, touched, errors } = formik;

    const handleAcceptedFiles = (acceptedFiles) => {
        const newFiles = acceptedFiles.map((file) => {
            const fileExt = file.name.split('.').pop().toLowerCase();
            return {
                file,
                preview: URL.createObjectURL(file),
                formattedSize: formatBytes(file.size),
                name: file.name, // Original name without modification
                originalName: file.name, // Store original name for reference
                fileType: fileExt
            };
        });

        setPreviews(prevPreviews => {
            const newPreviews = [...prevPreviews, ...newFiles];
            setFieldValue(name, newPreviews);
            return newPreviews;
        });

        setFieldTouched(name, true);
        setIsDragActive(false);
    };

    const handleRemoveFile = (fileToRemove) => {
        Swal.fire({
            title: "Are you sure?",
            text: "Do you want to remove this file?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, remove it!",
        }).then((result) => {
            if (result.isConfirmed) {
                setPreviews(prevPreviews => {
                    const updatedPreviews = prevPreviews.filter(item =>
                        item.preview !== fileToRemove.preview &&
                        item.originalUrl !== fileToRemove.originalUrl
                    );
                    setFieldValue(name, updatedPreviews);
                    return updatedPreviews;
                });

                if (fileToRemove.preview && typeof fileToRemove.preview !== 'string') {
                    URL.revokeObjectURL(fileToRemove.preview);
                }

                // Clear preview if the removed file was selected
                if (selectedFileForPreview &&
                    (selectedFileForPreview.preview === fileToRemove.preview ||
                        selectedFileForPreview.originalUrl === fileToRemove.originalUrl)) {
                    setSelectedFileForPreview(null);
                }

                Swal.fire("Removed!", "The file has been removed.", "success");
            }
        });
    };

    const handlePreviewFile = (file) => {
        setSelectedFileForPreview(file);
    };

    // New functions for editing filenames
    const openEditModal = (file, index) => {
        setEditingFile(file);
        setEditedFileName(file.name || '');
        setFileIndex(index);
        setEditModalOpen(true);
    };

    const saveEditedFileName = () => {
        if (!editedFileName.trim()) {
            return; // Don't save empty filenames
        }

        setPreviews(prevPreviews => {
            const updatedPreviews = [...prevPreviews];

            // Keep the file extension from the original name
            const originalExt = editingFile.originalName ?
                `.${editingFile.originalName.split('.').pop()}` :
                editingFile.fileType ? `.${editingFile.fileType}` : '';

            // Make sure the new name has the same extension
            let newName = editedFileName;
            if (originalExt && !newName.endsWith(originalExt)) {
                // Remove any extension the user might have added
                newName = newName.split('.')[0] + originalExt;
            }

            updatedPreviews[fileIndex] = {
                ...updatedPreviews[fileIndex],
                name: newName
            };

            console.log(name, updatedPreviews)
            setFieldValue(name, updatedPreviews);
            return updatedPreviews;
        });

        setEditModalOpen(false);
    };

    const meta = {
        touched: getIn(touched, name),
        error: getIn(errors, name),
    };

    // Display a clean filename without query parameters
    const getDisplayName = (file) => {
        return file.name || cleanFileName(file?.originalUrl?.split('/').pop() || '') || 'Unnamed File';
    };

    return (
        <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2 text-center w-100">
                {label}
            </label>

            <Dropzone
                onDrop={handleAcceptedFiles}
                onDragEnter={() => setIsDragActive(true)}
                onDragLeave={() => setIsDragActive(false)}
            >
                {({ getRootProps, getInputProps }) => (
                    <div
                        {...getRootProps()}
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-300 ease-in-out
                            ${isDragActive ? 'border-blue-500 bg-blue-50 scale-105' : 'border-gray-300 hover:border-blue-400'}
                            ${meta.touched && meta.error ? 'border-red-500' : ''}`}
                    >
                        <input {...getInputProps()} />
                        <div className={`${previews.length > 0 ? 'd-none' : ''}`}>
                            <svg
                                className={`
                                    mx-auto h-16 w-16 
                                    transition-colors duration-300
                                    ${isDragActive ? 'text-blue-500' : 'text-gray-400'}
                                `}
                                width={120}
                                stroke="currentColor"
                                fill="none"
                                viewBox="0 0 48 48"
                            >
                                <path
                                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>

                        <p className={`
                            text-sm transition-colors duration-300
                            ${isDragActive ? 'text-blue-600' : 'text-gray-600'}
                        `}>
                            {isDragActive
                                ? 'Drop files here'
                                : 'Drag and drop files or click to upload'}
                        </p>
                    </div>
                )}
            </Dropzone>

            <div className="mt-4">
                {/* Document Preview Section */}
                {selectedFileForPreview && (
                    <div className="mb-4 border rounded-lg overflow-hidden">
                        <DocumentPreviewUI
                            documentUrl={selectedFileForPreview.originalUrl || selectedFileForPreview.name}
                            previewURL={selectedFileForPreview.preview}
                        />
                    </div>
                )}

                {/* File List */}
                <div className="dropzone-previews" id="file-previews">
                    {previews.length > 0 && previews.map((f, i) => (
                        <Card className="mt-1 mb-0 shadow-sm border dz-processing dz-image-preview dz-success dz-complete" key={i + "-file"}>
                            <div className="p-2">
                                <Row className="align-items-center">
                                    <Col>
                                        <div className="text-muted font-weight-bold">{getDisplayName(f)}</div>
                                        <p className="mb-0"><strong>{f.formattedSize || ''}</strong></p>
                                    </Col>
                                    <Col className="col-auto d-flex">
                                        {/* Edit Button */}
                                        <Button
                                            color="info me-2"
                                            size="sm"
                                            onClick={() => openEditModal(f, i)}
                                        >
                                            <FeatherIcon icon="edit" size={16} />
                                        </Button>

                                        {/* Preview Button */}
                                        {canPreviewFile(f) && (
                                            <Button
                                                color="primary me-2"
                                                size="sm"
                                                onClick={() => {
                                                    if (selectedFileForPreview === f) {
                                                        setSelectedFileForPreview(null);
                                                    } else {
                                                        handlePreviewFile(f);
                                                    }
                                                }}
                                            >
                                                <FeatherIcon icon={selectedFileForPreview === f ? "eye-off" : "eye"} size={16} />
                                            </Button>
                                        )}

                                        {/* Delete Button */}
                                        <Button color="danger" size="sm" onClick={() => handleRemoveFile(f)}>
                                            <FeatherIcon icon="trash-2" size={16} />
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                        </Card>
                    ))}
                </div>
            </div>

            {meta.touched && meta.error && (
                <div className="text-red-500 text-sm bg-red-50 p-2 row text-center w-100 flex items-center space-x-2 mt-3">
                    <div className="text-danger">{meta.error}
                        <svg className="h-5 w-5 text-red-500 inline-block ml-1" fill="currentColor" viewBox="0 0 20 20" width={20}>
                            <path fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </div>
                </div>
            )}

            {/* Edit Filename Modal */}
            <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(!editModalOpen)}>
                <ModalHeader toggle={() => setEditModalOpen(!editModalOpen)}>
                    Edit Filename
                </ModalHeader>
                <ModalBody>
                    <div className="form-group">
                        <label htmlFor="filename" className="form-label">
                            File name:
                        </label>
                        <Input
                            type="text"
                            className="form-control"
                            id="filename"
                            value={editedFileName}
                            onChange={(e) => setEditedFileName(e.target.value)}
                        />
                        <small className="form-text text-muted">
                            The file extension will be preserved automatically.
                        </small>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setEditModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={saveEditedFileName}
                        disabled={!editedFileName.trim()}
                    >
                        Save
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export { DocumentsHandler };